import {
  Component,
  OnDestroy,
  ElementRef,
  inject,
  viewChild,
  TemplateRef,
  afterNextRender,
  effect,
  Signal,
  signal,
  DestroyRef,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Timeline, DataGroup, DataSet, DataItem, TimelineItem, TimelineOptions } from "vis-timeline/standalone";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatDialog } from "@angular/material/dialog";
import {
  BehaviorSubject,
  catchError,
  delay,
  EMPTY,
  filter,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from "rxjs";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import mock from "../../../assets/items.json";
import { trigger, state, style, transition, animate } from "@angular/animations";
import moment from "moment";
import "moment-timezone";
import { ExportRequestDTO, ExportJob } from "./model";
import { InfoManagementService } from "../service/info-management.service";
import { setLoader } from "src/app/utils/set-loaders";
import { ConversionStatus } from "./enums";
import { saveAs } from "file-saver";
import { MatCalendarCellClassFunction } from "@angular/material/datepicker";
import { MatSort } from "@angular/material/sort";

export const MY_FORMATS = {
  parse: {
    dateInput: "LL",
  },
  display: {
    dateInput: "DD MMM YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

@Component({
  selector: "app-data-export",
  templateUrl: "./data-export.component.html",
  styleUrls: ["./data-export.component.scss"],
  providers: [provideMomentDateAdapter(MY_FORMATS)],
  animations: [
    trigger("detailExpand", [
      state("collapsed,void", style({ height: "0px", minHeight: "0", visibility: "hidden" })),
      state("expanded", style({ height: "*", visibility: "visible" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4,0.0,0.2,1)")),
    ]),
  ],
})
export class DataExportComponent implements OnDestroy {
  private _destroyRef = inject(DestroyRef);

  private _fb = inject(FormBuilder);

  private _dialog = inject(MatDialog);

  private _infoManagementService = inject(InfoManagementService);

  private _confirmationTpl = viewChild<TemplateRef<any>>("confirmationTpl");

  private _timelineRef = viewChild<ElementRef>("timeline");

  private _paginatorSignal = viewChild<MatPaginator>(MatPaginator);

  private _matSortSignal = viewChild(MatSort);

  private _loaders = new BehaviorSubject({});

  loaders = toSignal(this._loaders);

  status = ConversionStatus;

  expandedElement: ExportJob | null = null;

  columnsToDisplay = ["name", "effectiveDate", "exportDate", "exportStatus", "actions"];

  icaoOptions = [
    { disabled: true, key: "aip", label: "AIP" },
    { disabled: true, key: "obstacle", label: "Obstacle" },
    { disabled: true, key: "aerodromeMapping", label: "Aerodrome Mapping" },
    { disabled: true, key: "ifp", label: "Instrument Flight Procedures" },
  ];

  postProccesingOptions = [
    { displayed: false, key: "removeFacilis", label: "Remove Facilis extensions" },
    { displayed: false, key: "copySrsName", label: "Copy default srsName to each GML object" },
    { displayed: false, key: "baselineOnly", label: "Baseline only" },
    { displayed: true, key: "facilisExt", label: "With Facilis Extension" },
    { displayed: true, key: "noFacilisExt", label: "Without Facilis Extension" },
  ];

  targetDatabaseOptions = [
    { label: "OPS - Operational", value: "OPS" },
    { label: "UN - Update Notice ", value: "UN" },
  ];

  temporaryChangesOptions = [
    {
      label: "Ignore",
      value: false,
      tooltipText: "Export permanent data (AIP baseline), not including temporary changes",
    },
    {
      label: "Apply",
      value: true,
      tooltipText:
        "Export the full operational situation at the selected effective date, including any then-active temporary changes",
    },
  ];

  postExportLookup = {
    facilisExt: "With Facilis Extension",
    noFacilisExt: "Without Facilis Extension",
  };

  iconLookup = {
    processing: "clock_loader_60",
    completed: "check_circle",
    failed: "cancel",
    timed_out: "error",
    not_started: "pending",
  };

  labelLookup = {
    processing: "Processing",
    completed: "Complete",
    failed: "Failed",
    timed_out: "Timed Out",
    not_started: "Not Started",
  };

  formatOptions = [
    { label: "AIXM 5.1", value: "5.1", displayed: true },
    { label: "AIXM 5.1.1", value: "5.1.1", displayed: true },
    { label: "AIXM 5.1.1 with Facilis Extension", value: "5.1.1-facilis", displayed: false },
  ];

  exportForm = new FormGroup({
    effectiveDate: new FormControl<moment.Moment | null>(null, { validators: [Validators.required] }),
    temporaryChanges: new FormControl<boolean>(false, { validators: [Validators.required] }),
    postProccessing: new FormControl<string>("", { validators: [Validators.required] }),
    icaoDataset: new FormControl<string>({ value: "", disabled: true }),
    format: new FormControl<string>({ value: this.formatOptions[1].value, disabled: false }),
  });

  get fc() {
    return this.exportForm.controls;
  }

  get icaoDatasetControl() {
    return this.fc.icaoDataset as unknown as FormGroup;
  }

  private _composeFormGroup(targetArray: any[]) {
    return targetArray.reduce((accumulator, { key, disabled }) => {
      if (!accumulator[key]) accumulator[key] = this._fb.control({ value: null, disabled: disabled ?? false });
      return accumulator;
    }, {});
  }

  private _reloader$ = new Subject();

  private _interval = 15;

  private _dataSourcePoll$ = this._reloader$.pipe(
    switchMap(() =>
      timer(0, this._interval * 1000).pipe(
        switchMap(() => {
          return this._infoManagementService.getDataExportJobs().pipe(setLoader(this._loaders, "getDataExportJobs"));
        }),
        catchError(() => EMPTY),
        filter((response) => !!response),
        map((response: ExportJob[]) => {
          const parse = response.map((value) => new ExportJob(value));
          const dataSource = new MatTableDataSource();
          dataSource.paginator = this._paginatorSignal();
          dataSource.sort = this._matSortSignal();
          dataSource.data = parse;
          return dataSource;
        }),
        takeWhile((result) => {
          return result.data.some((dataset: ExportJob) => dataset.exportStatus === this.status.PROCESSING);
        }, true),
        takeUntilDestroyed(this._destroyRef)
      )
    ),
    shareReplay(1)
  );

  dataSource = toSignal(this._dataSourcePoll$);

  countDown = toSignal(
    this._dataSourcePoll$.pipe(
      filter((source) => source.data.some((value: ExportJob) => value.exportStatus === this.status.PROCESSING)),
      switchMap(() => {
        return timer(0, 1000).pipe(
          map((tick) => this._interval - tick),
          takeWhile((val) => val > 0, true)
        );
      })
    )
  );

  // Timeline configuration
  enableTimeline: Signal<boolean> = signal(false);

  private _timeline: Timeline = null;

  private _timelineItems = toSignal(
    of(mock).pipe(
      delay(2000),
      map((result) => {
        const parsedItems: TimelineItem[] = result.map((item) => {
          const formatDate = (date) => moment(date).format(" MM/YY");
          const lookUp = {
            SUP: `<span>SUP${formatDate(item.start)}</span>`,
            AMDT: `
              <span>AMDT ${formatDate(item.start)} - ${
              item.end ? formatDate(item.end) : "Until Further Notice"
            }</span>`,
          };

          return {
            ...item,
            className: "timeline-item",
            content: lookUp[item.group],
            subgroup: item.group === "SUP" && "group-sup",
          };
        });
        return new DataSet(parsedItems);
      })
    )
  );

  private _initTimeline() {
    const container = this._timelineRef();

    const options: TimelineOptions = {
      width: "100%",
      height: "100%",
      margin: { item: { horizontal: 30 }, axis: 5 },
      align: "auto",
      showCurrentTime: true,
      orientation: { axis: "bottom" },
      min: moment().startOf("year").year(2017).toDate(),
      max: moment().add(10, "year").toDate(),
      zoomMin: 1000 * 60,
      stackSubgroups: true,
      stack: true,
    };

    // Create timeline ref
    const timeline = new Timeline(container.nativeElement, [], options);

    // Set timeline window position on first render
    const startPeriod = moment().subtract(14, "days").toDate();
    const endPeriod = moment().add(15, "days").toDate();
    timeline.setWindow(startPeriod, endPeriod);

    // Set timeline groups
    const timelineGroups: DataGroup[] = [
      { id: "SUP", content: "SUP", subgroupStack: true },
      { id: "AMDT", content: "AMDT", subgroupStack: true },
    ];
    timeline.setGroups(timelineGroups);

    // Timeline listener
    timeline.on("select", (props) => {
      if (props.items.length) {
        const itemId = props.items[0];
        const item = this._timelineItems().get(itemId) as unknown as DataItem;
        const dates = [item.start, item.end ?? null];
        const parsedDates = dates.map((date) => (date ? moment(date).format("DD MMM YYYY") : "Until Further Notice"));
        // this.fc.effectiveDate.setValue(parsedDates.join(" - "));
      }
    });

    return timeline;
  }

  constructor() {
    afterNextRender(() => {
      // Tick reloader to trigger get jobs history list on initial render
      this._reloader$.next(true);

      if (this.enableTimeline()) {
        this._timeline = this._initTimeline();
      }
    });

    effect(() => {
      if (this.enableTimeline() && this._timelineItems()) {
        this._timeline.setItems(this._timelineItems().get());
      }
    });
  }

  generateDataset() {
    if (this.exportForm.invalid) {
      this.exportForm.markAllAsTouched();
      return;
    }

    type ExporFormValue = ReturnType<typeof this.exportForm.getRawValue>;
    type ContentParser<T> = {
      [K in keyof T]?: (value: T[K]) => {
        label: string;
        type: string;
        content: any;
      };
    };
    const contentLookup: ContentParser<ExporFormValue> = {
      effectiveDate: (value) => ({
        label: "Effective Date",
        type: "string",
        content: `${value.format("DD MMM YYYY, HH:mm A z").toUpperCase()}`,
      }),
      temporaryChanges: (value) => ({
        label: "Temporary Changes",
        type: "boolean",
        content: value ? "Apply" : "Ignored",
      }),
      format: (value) => ({
        label: "Export Format",
        type: "string",
        content: `AIXM ${value}`,
      }),
      postProccessing: (value) => ({
        label: "Post-Export Processing Options",
        type: "string",
        content: this.postProccesingOptions.find((opt) => opt.key === value)?.label ?? "-",
      }),
      icaoDataset: (value) => ({
        label: "ICAO Dataset",
        type: "array",
        content: this.icaoOptions.filter((opt) => value[opt.key]),
      }),
    };

    const contentEntries = Object.entries(this.exportForm.value).reduce((accumulator, [key, value]) => {
      if (contentLookup[key]) {
        accumulator.push(contentLookup[key](value));
      }
      return accumulator;
    }, []);

    const dialogRef = this._dialog.open(this._confirmationTpl(), {
      width: "420px",
      disableClose: true,
      data: { contentEntries },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((closed) => !!closed),
        switchMap(() => {
          const payload = ExportRequestDTO.parseToPayload(this.exportForm.getRawValue());
          console.log({ contentEntries, formValue: payload }, "### contentEntries");
          return this._infoManagementService.startDataExportJob(payload).pipe(
            catchError(() => EMPTY),
            tap(() => {
              this.resetExportForm();
              this._reloader$.next(true);
            })
          );
        })
      )
      .subscribe();
  }

  downloadDataset(job: ExportJob) {
    this._infoManagementService
      .downloadDataset(job.datasetUuid)
      .pipe(
        tap((response) => {
          const blob = response.body as Blob;
          const fileName = `${job.datasetUuid}.zip`;
          saveAs(blob, fileName);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  fitTimeLine(): void {
    this._timeline.fit();
  }

  resetExportForm() {
    this.fc.effectiveDate.reset();
    this.fc.postProccessing.reset();
    this.fc.temporaryChanges.setValue(false);
  }

  onTimeChange(event: Event, controlKey: string) {
    const input = event.target as HTMLInputElement;
    const [hour, minutes] = input.value.split(":");
    const targetDate = this.fc[controlKey];
    targetDate.setValue(moment(targetDate.value).tz("utc").set({ hour: +hour, minutes: +minutes }));
  }

  ngOnDestroy(): void {
    if (this._timeline) {
      this._timeline.destroy();
    }
  }
}
